import React, { useState } from 'react';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { useUrlState } from '../../hooks/useUrlState';
import { Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUsers } from '../../hooks/useUsers';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Table } from '../../components/common/Table';
import { Avatar } from '../../components/common/Avatar';
import { Badge } from '../../components/common/Badge';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState } from '../../components/common/EmptyState';
import { ExportModal } from '../../components/common/ExportModal';
import type { UserRole, UserStatus } from '../../types/api';

// Row type for user table
interface UserRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture?: string | null;
  department?: string | null;
  position?: string | null;
  role: UserRole;
  status: UserStatus;
}

export const UserManagement: React.FC = () => {
  const openWithReturn = useOpenWithReturn();
  const { user: currentUser } = useAuth();
  // Search, role and status live in the URL, so opening a user and coming Back keeps them.
  const [view, setView] = useUrlState(
    { q: '', role: 'all', status: 'all' },
    { role: ['all', ...['account_admin', 'manager', 'employee', 'viewer']], status: ['all', ...['active', 'invited', 'inactive']] }
  );
  const searchTerm = view.q;
  const roleFilter = view.role as UserRole | 'all';
  const statusFilter = view.status as UserStatus | 'all';
  const setSearchTerm = (q: string) => setView({ q });
  const setRoleFilter = (role: UserRole | 'all') => setView({ role });
  const setStatusFilter = (status: UserStatus | 'all') => setView({ status });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { data, isLoading, error, refetch } = useUsers({
    search: searchTerm,
    role: roleFilter !== 'all' ? roleFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined
  });

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const canInviteUsers = currentUser.role === 'account_admin' || currentUser.role === 'manager';
  // The roster export (names, emails, roles, last-login) is admin-only on the server.
  // Managers can reach this page to invite users, so gate the button to match.
  const canExportUsers = currentUser.role === 'account_admin';

  const getRoleBadgeVariant = (role: string): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' => {
    switch (role) {
      case 'account_admin':
        return 'primary';
      case 'manager':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'invited':
        return 'warning';
      case 'inactive':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'User',
      render: (row: UserRow) => (
        <div className="flex items-center space-x-3">
          <Avatar
            src={row.profilePicture}
            alt={`${row.firstName} ${row.lastName}`}
            size="sm"
          />
          <div>
            <p className="font-medium text-stone-900">
              {row.firstName} {row.lastName}
            </p>
            {/*
              Managers reach this page but only receive email addresses for their
              own direct reports, so the field is absent for most rows. Render the
              department instead of an empty line.
            */}
            {(row.email || row.department) && (
              <p className="text-sm text-stone-500">{row.email || row.department}</p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'department',
      header: 'Department',
      render: (row: UserRow) => (
        <span className="text-stone-900">{row.department || '-'}</span>
      )
    },
    {
      key: 'position',
      header: 'Position',
      render: (row: UserRow) => (
        <span className="text-stone-900">{row.position || '-'}</span>
      )
    },
    {
      key: 'role',
      header: 'Role',
      render: (row: UserRow) => (
        <Badge variant={getRoleBadgeVariant(row.role)}>
          {row.role}
        </Badge>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: UserRow) => (
        <Badge variant={getStatusBadgeVariant(row.status)}>
          {row.status}
        </Badge>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row: UserRow) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openWithReturn(`/users/${row.id}`)}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Header - Warm gradient */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Team Members</h1>
              <p className="mt-1 text-sm text-stone-300">
                Manage your organization's users and invitations
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canExportUsers && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Download}
                  onClick={() => setIsExportModalOpen(true)}
                  className="bg-white text-stone-800 hover:bg-stone-100"
                >
                  Export
                </Button>
              )}
              {canInviteUsers && (
                <Button
                  variant="secondary"
                  onClick={() => openWithReturn('/users/invite')}
                  className="bg-white text-stone-800 hover:bg-stone-100"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                  Invite User
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <Card className="p-6">
          {/* Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="all">All Roles</option>
              <option value="account_admin">Account Admin</option>
              <option value="manager">Manager</option>
              <option value="employee">Employee</option>
              <option value="viewer">Viewer</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as UserStatus | 'all')}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Stats */}
          {data && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-primary-50 p-4 rounded-xl border border-primary-100">
                <p className="text-sm text-primary-600 font-medium">Total Users</p>
                <p className="text-2xl font-bold text-primary-700">{data.total}</p>
              </div>
              <div className="bg-success-50 p-4 rounded-xl border border-success-100">
                <p className="text-sm text-success-600 font-medium">Active</p>
                <p className="text-2xl font-bold text-success-700">
                  {data.users?.filter(u => u.status === 'active').length || 0}
                </p>
              </div>
              <div className="bg-warning-50 p-4 rounded-xl border border-warning-100">
                <p className="text-sm text-warning-600 font-medium">Invited</p>
                <p className="text-2xl font-bold text-warning-700">
                  {data.users?.filter(u => u.status === 'invited').length || 0}
                </p>
              </div>
              <div className="bg-stone-100 p-4 rounded-xl border border-stone-200">
                <p className="text-sm text-stone-600 font-medium">Account Admins</p>
                <p className="text-2xl font-bold text-stone-700">
                  {data.users?.filter(u => u.role === 'account_admin').length || 0}
                </p>
              </div>
            </div>
          )}

          {/* Table */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-danger-600 mb-4">Failed to load users</p>
              <Button variant="secondary" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          ) : data && data.users && data.users.length > 0 ? (
            <Table columns={columns} data={data.users} />
          ) : (
            <EmptyState
              title="No users found"
              description="Try adjusting your filters or invite new users to your organization."
              action={
                canInviteUsers
                  ? { label: 'Invite User', onClick: () => openWithReturn('/users/invite') }
                  : undefined
              }
            />
          )}
        </Card>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        defaultType="users"
        availableTypes={['users']}
        title="Export Users"
      />
    </div>
  );
};
